import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { Button } from "wiz-front-shared/components/button/Button";
import { Input, InputInfoMsg } from "components/common/input/Input";
import { recoverDataAtom, pushRecoverStepAtom } from "store/recoverStore";
import { userStore } from "store/userStore";
import './Recover.css';

// Step5: 새 비밀번호 입력 (provider=EMAIL 일 때만 도달) — 기존 Step4
export const RecoverStep5 = () => {
    const { t } = useTranslation();
    const data = useAtomValue(recoverDataAtom);
    const [, setData] = useAtom(recoverDataAtom);
    const pushStep = useSetAtom(pushRecoverStepAtom);
    const resetPasswordState = useAtomValue(userStore.resetPassword.stateAtom);

    const [pwd, setPwd] = useState("");
    const [pwdConfirm, setPwdConfirm] = useState("");
    const [pwdInfoMsg, setPwdInfoMsg] = useState<InputInfoMsg>();
    const [pwdConfirmInfoMsg, setPwdConfirmInfoMsg] = useState<InputInfoMsg>();
    const [submitting, setSubmitting] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");

    // 비밀번호 검증 (8자 이상 + 영문/숫자/특수문자 — SignUpStep6 과 동일 정책)
    const isPwdValid = useMemo(() => {
        if (!pwd) return false;
        if (pwd.length < 8) return false;
        const hasLetter = /[a-zA-Z]/.test(pwd);
        const hasNumber = /[0-9]/.test(pwd);
        const hasSpecial = /[!@#$%^&*(),.?":{}|<>_\-+=\[\]\\\/`~';]/.test(pwd);
        return hasLetter && hasNumber && hasSpecial;
    }, [pwd]);

    useEffect(() => {
        if (!pwd) {
            setPwdInfoMsg(undefined);
        } else if (pwd.length < 8) {
            setPwdInfoMsg({ msg: t("signUp.step6.passwordErrorMsg1"), type: "error" });
        } else if (!isPwdValid) {
            setPwdInfoMsg({ msg: t("signUp.step6.passwordErrorMsg2"), type: "error" });
        } else {
            setPwdInfoMsg(undefined);
        }
    }, [pwd, isPwdValid, t]);

    const isPwdMatch = useMemo(() => !!pwd && pwd === pwdConfirm, [pwd, pwdConfirm]);
    useEffect(() => {
        if (!pwdConfirm) {
            setPwdConfirmInfoMsg(undefined);
        } else if (!isPwdMatch) {
            setPwdConfirmInfoMsg({ msg: t("signUp.step6.passwordNotMatch"), type: "error" });
        } else {
            setPwdConfirmInfoMsg(undefined);
        }
    }, [pwdConfirm, isPwdMatch, t]);

    // recoveryToken 이 없으면 제출 불가 — 빈 토큰으로 reset-password 호출 방지
    const canSubmit = isPwdValid && isPwdMatch && !submitting && !!data.recoveryToken;

    // 비정상 진입(토큰 미발급/유실) 시 안내
    useEffect(() => {
        if (!data.recoveryToken) {
            setErrorMsg(t("recover.step4.tokenExpired"));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleSubmit = () => {
        if (!canSubmit) return;
        setErrorMsg("");
        setSubmitting(true);
        userStore.onResetPassword({
            recoveryToken: data.recoveryToken,
            newPassword: pwd,
        });
    };

    useEffect(() => {
        if (!submitting) return;
        if (resetPasswordState.status === "ready") {
            setSubmitting(false);
            userStore.resetPassword.onDefault();
            setData(prev => ({ ...prev, recoveryToken: "" }));   // 일회용 — 즉시 invalidate
            pushStep(6);
        } else if (resetPasswordState.status === "error") {
            setSubmitting(false);
            const code = resetPasswordState.error?.code;
            const msg = resetPasswordState.error?.message;
            setErrorMsg(
                code === "RECOVERY_TOKEN_EXPIRED"
                    ? t("recover.step4.tokenExpired")
                    : (msg || t("recover.step4.errorDefault"))
            );
            userStore.resetPassword.onDefault();
        }
    }, [resetPasswordState.status, resetPasswordState.error, submitting, pushStep, setData, t]);

    return (
        <div className="recover-step">
            <span className="title">{t("recover.step4.title")}</span>
            <span className="info">{t("recover.step4.info")}</span>

            <Input
                id="recover-pwd"
                inputVal={pwd}
                setInputVal={setPwd}
                type="password"
                title={t("signUp.step6.password")}
                placeholder={t("signUp.step6.passwordPlaceHolder")}
                infoMsg={pwdInfoMsg}
                isRequired
                showClearButton={false}
                maxLength={50}
            />

            <Input
                id="recover-pwd-confirm"
                inputVal={pwdConfirm}
                setInputVal={setPwdConfirm}
                type="password"
                placeholder={t("signUp.step6.passwordConfirmPlaceHolder")}
                infoMsg={pwdConfirmInfoMsg}
                showClearButton={false}
                maxLength={50}
            />

            {errorMsg && (
                <div style={{ marginTop: 12, fontSize: 13, color: "var(--warning)" }}>{errorMsg}</div>
            )}

            <div className="recover-bottom-btn">
                <Button
                    text={submitting ? t("signUp.loading") : t("recover.step4.submitBtn")}
                    onPress={handleSubmit}
                    disabled={!canSubmit}
                />
            </div>
        </div>
    );
};
